import React from 'react';
import styles from './styles.module.scss';

interface CollapsibleGroupBaseProps {
  /** DOM id base — builds `${id}-title` and `${id}-body` for ARIA wiring. */
  id: string;
  /** Item count badge, shown only while collapsed. */
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId?: string;
  toggleTestId?: string;
}

/** Default: the title slot and a separate caret toggle button. */
interface SplitHeaderProps {
  headerAsToggle?: false;
  /** Header content; may be interactive (e.g. a Link) — the toggle is a separate button. */
  title: React.ReactNode;
  /** Already-translated, state-aware accessible name of the toggle button. */
  toggleLabel: string;
}

/** The whole header is one toggle button; its visible title is its accessible name. */
interface ToggleHeaderProps {
  headerAsToggle: true;
  /** Non-interactive header content (it is rendered inside the button). */
  title: React.ReactNode;
  toggleLabel?: never;
}

export type CollapsibleGroupProps = CollapsibleGroupBaseProps & (SplitHeaderProps | ToggleHeaderProps);

export interface GroupFrameProps {
  children: React.ReactNode;
  /** Optional static (non-collapsible) header in the same tinted strip as `CollapsibleGroup`. */
  title?: React.ReactNode;
  testId?: string;
}

/**
 * Bordered card chrome shared by `CollapsibleGroup` — for lists that need the
 * same frame without the collapse behaviour: header-less (e.g. a single
 * time-sorted list) or with a static `title` header.
 */
export const GroupFrame: React.FC<GroupFrameProps> = ({ children, title, testId }) => (
  <section className={styles.frame} data-testid={testId}>
    {title !== undefined && (
      <div className={`${styles.header} ${styles.headerStatic}`}>
        <div className={styles.title}>{title}</div>
      </div>
    )}
    {children}
  </section>
);

/**
 * Controlled expandable card: header (title slot + count badge + caret toggle,
 * or — with `headerAsToggle` — one full-width toggle button) over a body that
 * collapses via `grid-template-rows: 1fr → 0fr`. The collapsed body is also
 * `visibility: hidden` (after the animation) so its content leaves the tab
 * order and the accessibility tree without JS.
 */
export const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  id,
  title,
  count,
  expanded,
  onToggle,
  toggleLabel,
  headerAsToggle,
  children,
  testId,
  toggleTestId,
}) => {
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;

  const indicators = (
    <>
      {count !== undefined && (
        <span className={styles.badge} aria-hidden="true">{count}</span>
      )}
      <i className={`icon-caret-down ${styles.caret}`} aria-hidden="true" />
    </>
  );

  return (
    <section
      className={`${styles.frame}${expanded ? ` ${styles.expanded}` : ''}`}
      data-testid={testId}
    >
      {headerAsToggle ? (
        <button
          type="button"
          className={`${styles.header} ${styles.headerToggle}`}
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggle}
          data-testid={toggleTestId}
        >
          <span id={titleId} className={styles.title}>
            {title}
          </span>
          <span className={styles.indicators}>{indicators}</span>
        </button>
      ) : (
        <div className={styles.header}>
          <div id={titleId} className={styles.title}>
            {title}
          </div>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            aria-label={toggleLabel}
            onClick={onToggle}
            data-testid={toggleTestId}
          >
            {indicators}
          </button>
        </div>
      )}

      <div id={bodyId} role="region" aria-labelledby={titleId} className={styles.body}>
        <div className={styles.bodyInner}>{children}</div>
      </div>
    </section>
  );
};

export default CollapsibleGroup;
